import React, { useEffect, useMemo, useRef, useState } from 'react'
import Modal from 'react-bootstrap/Modal'
import Form from 'react-bootstrap/Form'
import Row from 'react-bootstrap/Row'
import Col from 'react-bootstrap/Col'
import { toast } from 'react-toastify'
import ActionButton from 'shared/buttons/action-button'
import { faPen } from '@fortawesome/free-solid-svg-icons'
import { errorMessage } from 'admin/shared/errors'
import useMunicipalitySettings from 'api/hooks/use-municipality-settings'
import {
  useAccountGroup,
  useAccountGroups,
  useCreateAccountGroup,
  useUpdateAccountGroup
} from 'api/hooks/use-account-groups'
import { useAccountGroupings } from 'api/hooks/use-account-groupings'
import MemberPicker from './member-picker'
import {
  SEGMENT_SCOPES,
  WHOLE_ACCOUNT_SCOPE,
  segmentLabel,
  groupLabel
} from './scopes'

// Create/edit/view modal for an account group. `group` is null when creating;
// `readOnly` renders the same layout with its inputs locked, so View and Edit stay
// one component and can't drift apart.
//
// A new group is filed under `groupingId`, the taxonomy on screen. A saved group's is fixed:
// its codes may already be claimed in the destination, so moving one is a delete-and-recreate.
const GroupModal = ({ show, group, groupingId, onHide, readOnly = false }) => {
  // View mode can be promoted to edit from the header, so `readOnly` is the mode the
  // modal opened in and `locked` is the mode it is in now. Reset on close, below,
  // so reopening a group with View doesn't land in edit.
  const [editRequested, setEditRequested] = useState(false)
  const locked = readOnly && !editRequested
  const isEdit = Boolean(group?.id) && !locked

  const [code, setCode] = useState('')
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [groupType, setGroupType] = useState('account_code')
  const [accountType, setAccountType] = useState('object')
  const [memberIds, setMemberIds] = useState([])

  // The edit form needs the members, which only the show endpoint returns.
  const { data: detail } = useAccountGroup({ id: group?.id })

  // Locks candidates already claimed, rather than surfacing the conflict as a 422 on save.
  // Scoped to this group's own grouping: locking OCAS's claims would hide half the chart of
  // accounts from an ACFR group.
  const activeGroupingId = group?.account_grouping_id || groupingId
  const { data: groupingGroups } = useAccountGroups({
    groupingId: activeGroupingId,
    options: { enabled: Boolean(activeGroupingId) }
  })
  // Segment display names are municipality-configured.
  const { data: settings } = useMunicipalitySettings({ options: {} })
  const { data: groupings } = useAccountGroupings({})
  const groupingName = (groupings || []).find(
    (grouping) => grouping.id === activeGroupingId
  )?.name
  const scopeKey =
    groupType === WHOLE_ACCOUNT_SCOPE ? WHOLE_ACCOUNT_SCOPE : accountType

  // Seed the form once per open. Tracked with a ref rather than split across two
  // effects because the modal stays mounted between opens: clearing memberIds on
  // open and re-filling them from a `[detail]`-keyed effect loses the members on
  // reopen, since react-query hands back the same object reference for an
  // unchanged cached response so that effect never re-fires. Saving then would
  // PATCH member_ids: [] and delete every member.
  const seededFor = useRef(null)

  useEffect(() => {
    if (!show) {
      seededFor.current = null
      setEditRequested(false)
      return
    }

    const key = group?.id || 'new'
    if (seededFor.current === key) return
    // On edit, wait for the members to arrive before seeding.
    if (group?.id && !detail) return

    seededFor.current = key
    setCode(group?.code || '')
    setName(group?.name || '')
    setDescription(group?.description || '')
    setGroupType(group?.group_type || 'account_code')
    setAccountType(group?.account_type || 'object')
    setMemberIds((detail?.members || []).map((member) => member.member_id))
  }, [show, group, detail])

  const onError = (err) =>
    toast.error(errorMessage(err, 'Could not save the account group'))
  const onSuccess = () => {
    toast.success(isEdit ? 'Account group updated' : 'Account group created')
    onHide()
  }

  const { mutate: createGroup, isLoading: creating } = useCreateAccountGroup({
    options: { onSuccess, onError }
  })
  const { mutate: updateGroup, isLoading: updating } = useUpdateAccountGroup({
    options: { onSuccess, onError }
  })

  const params = useMemo(
    () => ({
      code,
      name,
      description,
      group_type: groupType,
      // The API rejects an account_type on whole-account groups.
      account_type: groupType === WHOLE_ACCOUNT_SCOPE ? '' : accountType,
      // Ignored on update — the API drops it.
      account_grouping_id: activeGroupingId,
      member_ids: memberIds
    }),
    [code, name, description, groupType, accountType, activeGroupingId, memberIds]
  )

  // { member_id => owning group label }, excluding the group being edited.
  const alreadyGrouped = useMemo(() => {
    const owners = {}

    ;(groupingGroups || [])
      .filter(
        (candidate) =>
          candidate.scope_key === scopeKey && candidate.id !== group?.id
      )
      .forEach((candidate) => {
        ;(candidate.member_ids || []).forEach((memberId) => {
          owners[memberId] = groupLabel(candidate)
        })
      })

    return owners
  }, [groupingGroups, scopeKey, group?.id])

  // The show endpoint already returns each saved member with a display label, so
  // the picker can name selected members without pinning them into its paged
  // query (which used to overrun the request line for large groups).
  const selectedLabels = useMemo(() => {
    const map = {}
    ;(detail?.members || []).forEach((member) => {
      map[member.member_id] = member.label
    })
    return map
  }, [detail])

  const handleSave = () => {
    if (isEdit) {
      updateGroup({ id: group.id, params })
    } else {
      createGroup(params)
    }
  }

  // Changing the dimension invalidates any members already picked.
  const handleGroupTypeChange = (value) => {
    setGroupType(value)
    setMemberIds([])
  }

  const handleAccountTypeChange = (value) => {
    setAccountType(value)
    setMemberIds([])
  }

  return (
    <Modal show={show} onHide={onHide} size="lg">
      <Modal.Header closeButton>
        <div className="d-flex align-items-center gap-3">
          <Modal.Title>
            {locked
              ? 'Account Group'
              : isEdit
                ? 'Edit Account Group'
                : 'Add Account Group'}
          </Modal.Title>
          {locked && (
            <ActionButton
              label="Edit"
              icon={faPen}
              variant="light"
              className="text-primary"
              onClick={() => setEditRequested(true)}
            />
          )}
        </div>
      </Modal.Header>
      <Modal.Body>
        <Row className="mb-3">
          <Col sm={3}>
            <span className="text-primary">Code</span>
          </Col>
          <Col sm={9}>
            <Form.Control
              type="text"
              value={code}
              disabled={locked}
              placeholder="100"
              onChange={(e) => setCode(e.target.value)}
            />
            <Form.Text muted>
              The rollup’s own number, kept separate from its name the way an
              account code is. Optional — leave it blank for a group that isn’t
              part of a numbered series.
            </Form.Text>
          </Col>
        </Row>

        <Row className="mb-3">
          <Col sm={3}>
            <span className="text-primary">Name</span>
          </Col>
          <Col sm={9}>
            <Form.Control
              type="text"
              required
              value={name}
              disabled={locked}
              placeholder="Salaries & Benefits"
              onChange={(e) => setName(e.target.value)}
            />
            <Form.Text muted>
              Grouped codes report under this code and name; everything else
              keeps its own.
            </Form.Text>
          </Col>
        </Row>

        <Row className="mb-3">
          <Col sm={3}>
            <span className="text-primary">Description</span>
          </Col>
          <Col sm={9}>
            <Form.Control
              as="textarea"
              rows={6}
              value={description}
              disabled={locked}
              onChange={(e) => setDescription(e.target.value)}
            />
            <Form.Text muted>
              What the rollup covers. The report assistant reads this to judge
              whether a group answers a question, so say what is in and what is
              out.
            </Form.Text>
          </Col>
        </Row>

        <Row className="mb-3">
          <Col sm={3}>
            <span className="text-primary">Grouping</span>
          </Col>
          <Col sm={9}>
            <Form.Control type="text" value={groupingName || ''} disabled readOnly />
            <Form.Text muted>
              The taxonomy this rollup belongs to. Fixed once the group exists —
              its codes may already be rolled up differently in another grouping,
              so moving one is a delete and recreate.
            </Form.Text>
          </Col>
        </Row>

        <Row className="mb-3">
          <Col sm={3}>
            <span className="text-primary">Dimension</span>
          </Col>
          <Col sm={9}>
            <Form.Select
              value={groupType}
              disabled={isEdit || locked}
              onChange={(e) => handleGroupTypeChange(e.target.value)}
            >
              <option value="account_code">Account code segment</option>
              <option value={WHOLE_ACCOUNT_SCOPE}>Whole account</option>
            </Form.Select>
            {isEdit && (
              <Form.Text muted>
                The dimension can’t change after creation — create a new group
                instead.
              </Form.Text>
            )}
          </Col>
        </Row>

        {groupType === 'account_code' && (
          <Row className="mb-3">
            <Col sm={3}>
              <span className="text-primary">Segment</span>
            </Col>
            <Col sm={9}>
              <Form.Select
                value={accountType}
                disabled={isEdit || locked}
                onChange={(e) => handleAccountTypeChange(e.target.value)}
              >
                {SEGMENT_SCOPES.map((entry) => (
                  <option key={entry.scope} value={entry.scope}>
                    {segmentLabel(entry, settings)}
                  </option>
                ))}
              </Form.Select>
            </Col>
          </Row>
        )}

        <Row>
          <Col sm={3}>
            <span className="text-primary">Members</span>
          </Col>
          <Col sm={9}>
            <MemberPicker
              groupType={groupType}
              accountType={accountType}
              selected={memberIds}
              onChange={setMemberIds}
              alreadyGrouped={alreadyGrouped}
              selectedLabels={selectedLabels}
              readOnly={locked}
            />
          </Col>
        </Row>
      </Modal.Body>
      <Modal.Footer>
        <ActionButton
          label={locked ? 'Close' : 'Cancel'}
          variant="light"
          className="text-primary"
          onClick={onHide}
        />
        {!locked && (
          <ActionButton
            label="Save"
            variant="primary"
            loading={creating || updating}
            disabled={name.trim().length === 0}
            onClick={handleSave}
          />
        )}
      </Modal.Footer>
    </Modal>
  )
}

export default GroupModal
